import React, { memo, useCallback, useEffect, useRef } from 'react';

import { CanvasViewport, PlacementLayer, PlacementRect, PlanFrame, PlanImage, TransformLayer } from './styles';
import { PlacementDetailsPopover } from './placement-details-popover';
import { FloorplanZoomControls } from './zoom-controls';
import { useFloorplanZoom } from '../hooks/use-floorplan-zoom.hook';
import { FloorplanPlacement } from '../types';
import { geometryToPercentBox, imageAspectRatio } from '../utils/coordinates';
import { getPlacementStatus } from '../utils/placement-status';

interface FloorplanCanvasProps {
  /** Already resolved to a loadable src by the floorplans query. */
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  /** Used as the image's accessible name — the floorplan's name is the floor ("Floor 3"). */
  name: string;
  placements: FloorplanPlacement[];
  selectedResourceUuid?: string | null;
  /** A coworker jump's target: centred and zoomed on mount/change, drawn with the focus ring. */
  focusedResourceUuid?: string | null;
  dimmedResourceUuids?: Set<string>;
  /** Favorites highlight on: the favorites in this set get a gold ring (the rest arrive in `dimmedResourceUuids`). */
  highlightedFavoriteUuids?: ReadonlySet<string> | null;
  /** For the details card's star on the web popover. */
  favoriteResourceUuids?: ReadonlySet<string>;
  isFavoritesEnabled?: boolean;
  isFavoritesLoaded?: boolean;
  detailsPlacement?: FloorplanPlacement | null;
  onCloseDetails?: VoidFunction;
  onPlacementClick?: (resourceUuid: string) => void;
  /**
   * Coworker identity (the `team` colour state) may be drawn — `useCoworkerVisibility().effectiveName`.
   * The user's own booking is drawn regardless.
   */
  showCoworkerIdentity?: boolean;
  /**
   * Focus mode (§C3): the active coworker group's members. Set ⇒ only their desks read as coworkers
   * (ringed), other occupied desks go grey and free desks step back. Null/undefined ⇒ no focus.
   */
  focusGroupMemberUuids?: ReadonlySet<string> | null;
}

interface PlacementProps {
  placement: FloorplanPlacement;
  isSelected: boolean;
  isFocused: boolean;
  isDimmed: boolean;
  isFavoriteHighlighted: boolean;
  showCoworkerIdentity: boolean;
  focusGroupMemberUuids: ReadonlySet<string> | null;
  onClick?: (resourceUuid: string) => void;
}

const Placement = memo(function PlacementRectangle({
  placement,
  isSelected,
  isFocused,
  isDimmed,
  isFavoriteHighlighted,
  showCoworkerIdentity,
  focusGroupMemberUuids,
  onClick,
}: PlacementProps): JSX.Element {
  const { resource, geometry } = placement;
  const status = getPlacementStatus(placement, { showCoworkerIdentity, focusGroupMemberUuids });

  const handleClick = useCallback(() => onClick?.(resource.uuid), [onClick, resource.uuid]);

  return (
    <PlacementRect
      type="button"
      data-testid={`floorplan-placement-${resource.uuid}`}
      data-status={status}
      data-favorite={isFavoriteHighlighted || undefined}
      aria-label={resource.name}
      aria-pressed={isSelected}
      status={status}
      isSelected={isSelected}
      isFocused={isFocused}
      isDimmed={isDimmed}
      isFavoriteHighlighted={isFavoriteHighlighted}
      isFocusMode={!!focusGroupMemberUuids}
      disabled={isDimmed}
      style={geometryToPercentBox(geometry)}
      onClick={handleClick}
    />
  );
});

export const FloorplanCanvas = ({
  imageUrl,
  imageWidth,
  imageHeight,
  name,
  placements,
  selectedResourceUuid,
  focusedResourceUuid,
  dimmedResourceUuids,
  highlightedFavoriteUuids = null,
  favoriteResourceUuids,
  isFavoritesEnabled = false,
  isFavoritesLoaded = false,
  detailsPlacement,
  onCloseDetails,
  onPlacementClick,
  showCoworkerIdentity = false,
  focusGroupMemberUuids = null,
}: FloorplanCanvasProps): JSX.Element => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const planRef = useRef<HTMLDivElement>(null);
  const { transform, scale, isPannable, canZoomIn, canZoomOut, zoomIn, zoomOut, focusOn } = useFloorplanZoom(
    viewportRef,
    imageUrl,
    planRef,
  );

  // Centre and zoom on the jump target once it is on this floor's plan. Keyed on the uuid, not the
  // placement object, so a refetch of the same floor does not re-centre under the user's hands.
  const focusedGeometry = placements.find(({ resource }) => resource.uuid === focusedResourceUuid)?.geometry;
  const focusedKey = focusedResourceUuid && focusedGeometry ? focusedResourceUuid : null;

  useEffect(() => {
    if (focusedKey && focusedGeometry) {
      focusOn(focusedGeometry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- geometry is re-read only when the key changes
  }, [focusedKey, focusOn]);

  return (
    <CanvasViewport ref={viewportRef} data-testid="floorplan-canvas" isPannable={isPannable}>
      <TransformLayer style={{ transform }}>
        <PlanFrame ref={planRef}>
          <PlanImage
            src={imageUrl}
            alt={name}
            draggable={false}
            // Reserves the right box before the image loads, so the map does not jump into place.
            style={{ aspectRatio: `${imageAspectRatio(imageWidth, imageHeight)}` }}
          />
          <PlacementLayer>
            {placements.map(placement => (
              <Placement
                key={placement.resource.uuid}
                placement={placement}
                isSelected={placement.resource.uuid === selectedResourceUuid}
                isFocused={placement.resource.uuid === focusedResourceUuid}
                isDimmed={!!dimmedResourceUuids?.has(placement.resource.uuid)}
                isFavoriteHighlighted={
                  !!highlightedFavoriteUuids?.has(placement.resource.uuid) &&
                  !dimmedResourceUuids?.has(placement.resource.uuid)
                }
                showCoworkerIdentity={showCoworkerIdentity}
                focusGroupMemberUuids={focusGroupMemberUuids}
                onClick={onPlacementClick}
              />
            ))}
          </PlacementLayer>
          {detailsPlacement && onCloseDetails && (
            <PlacementDetailsPopover
              placement={detailsPlacement}
              scale={scale}
              onClose={onCloseDetails}
              isFavoritesEnabled={isFavoritesEnabled}
              isFavoritesLoaded={isFavoritesLoaded}
              favoriteResourceUuids={favoriteResourceUuids}
            />
          )}
        </PlanFrame>
      </TransformLayer>
      <FloorplanZoomControls canZoomIn={canZoomIn} canZoomOut={canZoomOut} onZoomIn={zoomIn} onZoomOut={zoomOut} />
    </CanvasViewport>
  );
};
